import type { ReactNode } from "react";
import isMatch from "lodash/isMatch";
import omit from "lodash/omit";
import pickBy from "lodash/pickBy";

export type FilterValues = Record<string, unknown>;

/**
 * One removable filter in the active-filter bar.
 *
 * `label` is a message key or a literal, translated by the bar. `content`
 * replaces the rendered label when a chip needs more than text (a coloured
 * tag badge); `label` still names the chip for screen readers.
 *
 * Each chip carries its own `remove`, so a chip that owns several keys (a
 * single-select spread over three keys) and a key that yields several chips
 * (one per selected tag) are just different `remove` functions.
 */
export type FilterChip = {
  id: string;
  label: string;
  labelArgs?: Record<string, unknown>;
  content?: ReactNode;
  remove: (values: FilterValues) => FilterValues;
};

export type FilterDescriptor = {
  /** Keys this descriptor accounts for. Anything unowned gets a generic chip. */
  keys: readonly string[];
  chips: (values: FilterValues) => FilterChip[];
};

const isSet = (value: unknown) => value !== undefined && value !== "";

/**
 * Chips in descriptor order, then one generic chip per key no descriptor owns.
 *
 * The order must not depend on which chip was removed last: removing the first
 * chip moves the next one into its place, so its X lands under the cursor.
 *
 * Unowned keys are never hidden. A filter the page forgot to describe is still
 * narrowing the list, and an invisible filter is exactly what this bar is for.
 */
export function buildFilterChips(
  values: FilterValues | undefined,
  descriptors: readonly FilterDescriptor[],
): FilterChip[] {
  const current = pickBy(values ?? {}, isSet);
  const owned = new Set(descriptors.flatMap((d) => d.keys));

  const described = descriptors.flatMap((d) => d.chips(current));
  const unknown = Object.keys(current)
    .filter((key) => !owned.has(key))
    .map(
      (key): FilterChip => ({
        id: `unknown:${key}`,
        label: `${humaniseFilterKey(key)}: ${formatFilterValue(current[key])}`,
        remove: (v) => omit(v, key),
      }),
    );

  return [...described, ...unknown];
}

/**
 * `loops_contact_id@not.is` → "Loops contact id". Cuts at the first `@`, since
 * PostgREST operators contain dots.
 */
export function humaniseFilterKey(key: string): string {
  const field = key.split("@")[0].replace(/_/g, " ").trim();
  return field.charAt(0).toUpperCase() + field.slice(1);
}

const formatFilterValue = (value: unknown) =>
  typeof value === "object" ? JSON.stringify(value) : String(value);

/** The free-text search box, shown as a chip like any other filter. */
export const searchDescriptor = (source = "q"): FilterDescriptor => ({
  keys: [source],
  chips: (values) =>
    isSet(values[source])
      ? [
          {
            id: `search:${source}`,
            label: "crm.filters.search",
            labelArgs: { term: values[source] },
            remove: (v) => omit(v, source),
          },
        ]
      : [],
});

export type FilterOption = {
  label: string;
  /** Same shape as a ToggleFilterButton value; `undefined` keys are ignored. */
  value: FilterValues;
  content?: ReactNode;
};

/**
 * Chips for a group of ToggleFilterButton-style options.
 *
 * An option is active when the filters match its defined keys, the rule
 * ToggleFilterButton already uses to highlight itself, so the chip and the
 * sidebar never disagree. Removing a chip omits every key in the group, which
 * is what a single-select spread over several keys needs (zoom host, action
 * item source) and is harmless for plain toggles.
 *
 * Not for values computed from the clock (a "today" bound recomputed at render
 * stops matching after midnight while the stored filter still applies). Those
 * need a descriptor that reads the stored value, like the zoom date range.
 */
export const optionsDescriptor = (
  keys: readonly string[],
  options: readonly FilterOption[],
): FilterDescriptor => ({
  keys,
  chips: (values) =>
    options
      .filter((option) => {
        const defined = pickBy(option.value, (v) => v !== undefined);
        // An option with nothing defined (a "Me" button before the identity
        // loads) would match every filter set.
        return Object.keys(defined).length > 0 && isMatch(values, defined);
      })
      .map((option) => ({
        id: `option:${keys.join(",")}:${JSON.stringify(option.value)}`,
        label: option.label,
        content: option.content,
        remove: (v) => omit(v, keys),
      })),
});
